import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, ArrowRight, MessageCircle, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InternationalPhoneInput, isValidPhoneNumber } from '@/components/ui/phone-input';
import { useLeadSubmit, LeadSource } from '@/hooks/useLeadSubmit';
import { useBrochureDownload } from '@/hooks/useBrochureDownload';
import { useFloorPlanDownload } from '@/hooks/useFloorPlanDownload';
import { projectData } from '@/data/project';
import { cn } from '@/lib/utils';
import { analytics } from '@/lib/analytics';

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'brochure' | 'price' | 'callback' | 'floorplan' | 'contact' | 'site_visit' | 'dossier' | 'exit_intent' | 'virtual_tour';
  ctaLabel?: string;
  onSuccess?: () => void;
  /** When type="floorplan", download this PDF after successful submission */
  downloadUrl?: string;
  /** Used to generate a friendly file name for the download */
  downloadName?: string;
}

const modalConfig: Record<string, { title: string; subtitle: string; cta: string }> = {
  brochure: {
    title: 'Get Project Overview',
    subtitle: "We'll share current planning and availability.",
    cta: 'Get Project Overview',
  },
  price: {
    title: 'Get Detailed Pricing',
    subtitle: "We'll share current planning and availability.",
    cta: 'Get Detailed Pricing',
  },
  callback: {
    title: 'Request Call Back',
    subtitle: "We'll share current planning and availability.",
    cta: 'Request Call Back',
  },
  floorplan: {
    title: 'Request Floor Plans',
    subtitle: "We'll share configuration and layout information.",
    cta: 'Request Floor Plans',
  },
  contact: {
    title: 'Get Project Overview',
    subtitle: "We'll share current planning and availability.",
    cta: 'Get Project Overview',
  },
  site_visit: {
    title: 'Schedule Site Visit',
    subtitle: "We'll coordinate your visit.",
    cta: 'Schedule Site Visit',
  },
  dossier: {
    title: 'Request Project Dossier',
    subtitle: "We'll share current planning and availability.",
    cta: 'Get Project Dossier',
  },
  exit_intent: {
    title: 'Request Project Guidance',
    subtitle: "We'll guide you based on the developer's planning.",
    cta: 'Request Project Guidance',
  },
  virtual_tour: {
    title: 'Watch Virtual Tour',
    subtitle: 'Enter your details to access the immersive 360° experience.',
    cta: 'Watch Virtual Tour',
  },
};

// Email validation removed - email is no longer collected

const LeadFormModal = ({ isOpen, onClose, type, ctaLabel, onSuccess, downloadUrl, downloadName }: LeadFormModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { downloadBrochure } = useBrochureDownload();
  const { downloadMasterLayout } = useFloorPlanDownload();
  const config = modalConfig[type] || modalConfig.contact;

  const downloadFromUrl = useCallback((url: string, label?: string) => {
    const safeLabel = (label || 'floor-plan').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const link = document.createElement('a');
    link.href = url;
    link.download = `nahar-chandi-valley-${safeLabel}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const { submitLead, isSubmitting, isSuccess, reset } = useLeadSubmit({
    source: type as LeadSource,
    onSuccess: () => {
      // Auto-download based on type
      if (type === 'brochure' || type === 'price' || type === 'contact' || type === 'exit_intent' || type === 'dossier') {
        downloadBrochure();
      } else if (type === 'floorplan') {
        if (downloadUrl) {
          downloadFromUrl(downloadUrl, downloadName);
        } else {
          downloadMasterLayout();
        }
      }
      // Call external onSuccess callback if provided
      onSuccess?.();
      // Reset form after 4 seconds and close
      setTimeout(() => {
        reset();
        setFormData({ name: '', phone: '', honeypot: '' });
        onClose();
      }, 4000);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({ name: '', phone: '', honeypot: '' });
      setErrors({});
    }
  }, [isOpen]);

  const validate = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = 'Full name is required';
    } else if (trimmedName.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (trimmedName.length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    } else if (/\d/.test(trimmedName)) {
      newErrors.name = 'Name should not contain numbers';
    }

    if (!formData.phone) {
      newErrors.phone = 'Mobile number is required';
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid mobile number';
    }

    if (formData.honeypot) {
      newErrors.honeypot = 'Spam detected';
    }

    setErrors(newErrors);
    return newErrors;
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    const hasErrors = Object.keys(validationErrors).length > 0;

    if (hasErrors) {
      Object.entries(validationErrors).forEach(([field, message]) => {
        if (message) {
          analytics.formError(`lead_form_${type}`, field, message);
        }
      });
      return;
    }

    await submitLead({
      name: formData.name,
      phone: formData.phone,
      honeypot: formData.honeypot,
    });
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(`Hi, I submitted an enquiry for ${projectData.projectName}. Please share details.`);
    window.open(`https://wa.me/${projectData.contact.whatsappNumber}?text=${message}`, '_blank');
  };

  // Check if form is valid for submit button state
  const isFormValid = formData.name.trim().length >= 2 && 
                      isValidPhoneNumber(formData.phone);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {isOpen && (
        <DialogContent className="sm:max-w-md bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-foreground">{config.title}</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">{config.subtitle}</p>
          </DialogHeader>

          {isSuccess ? (
            <div className="flex flex-col items-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="font-sans text-xl font-semibold text-foreground mb-2">Thank You!</h3>
              <p className="font-sans text-muted-foreground text-center mb-6">
                {(type === 'brochure' || type === 'price' || type === 'contact' || type === 'exit_intent' || type === 'dossier')
                  ? 'Your brochure download has started. Our team will contact you shortly.'
                  : type === 'floorplan'
                  ? 'Your floor plan download has started. Our team will contact you shortly.'
                  : 'Our team will contact you shortly. Check your email for confirmation.'}
              </p>

              <div className="flex flex-col gap-3 w-full max-w-xs">
                {(type === 'brochure' || type === 'price' || type === 'contact' || type === 'exit_intent' || type === 'dossier') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadBrochure}
                    className="font-sans gap-2 w-full"
                  >
                    <Download className="w-4 h-4" />
                    Download Again
                  </Button>
                )}
                {type === 'floorplan' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => (downloadUrl ? downloadFromUrl(downloadUrl, downloadName) : downloadMasterLayout())}
                    className="font-sans gap-2 w-full"
                  >
                    <Download className="w-4 h-4" />
                    Download Again
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleWhatsAppClick}
                  className="font-sans gap-2 w-full"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat on WhatsApp
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                value={formData.honeypot}
                onChange={(e) => handleChange('honeypot', e.target.value)}
                className="absolute -left-[9999px] opacity-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                aria-label="Leave this field empty"
              />

              {/* Full Name */}
              <div>
                <Input
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\b\w/g, (char) => char.toUpperCase());
                    handleChange('name', value);
                  }}
                  className={cn('font-sans', errors.name && 'border-destructive')}
                  maxLength={100}
                  autoComplete="name"
                  autoFocus
                />
                {errors.name && (
                  <p className="text-sm text-destructive mt-1 flex items-center gap-1 font-sans">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name}
                  </p>
                )}
              </div>


              {/* Phone */}
              <div>
                <InternationalPhoneInput
                  value={formData.phone}
                  onChange={(value) => handleChange('phone', value)}
                  error={!!errors.phone}
                  required
                />
                {errors.phone && (
                  <p className="text-sm text-destructive mt-1 flex items-center gap-1 font-sans">
                    <AlertCircle className="w-3 h-3" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Submit CTA */}
              <Button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 py-6 font-sans font-semibold text-base group disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {ctaLabel || config.cta}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>

              {/* Consent Text */}
              <p className="text-xs text-muted-foreground text-center font-sans leading-relaxed">
                By submitting, you agree to be contacted via call, WhatsApp, or SMS regarding {projectData.projectName}.{' '}
                <Link to="/privacy-policy" className="text-accent hover:underline">
                  Privacy Policy
                </Link>.
              </p>

              {/* MahaRERA Trust */}
              <div className="flex items-center justify-center gap-3 pt-2 border-t border-border/50">
                <img
                  src="/assets/maharera-qr-code.png"
                  alt="MahaRERA QR Code"
                  className="w-12 h-12 rounded"
                />
                <div className="text-left">
                  <p className="text-[10px] text-muted-foreground font-sans">MahaRERA Registered</p>
                  <p className="text-xs text-foreground font-sans font-medium">{projectData.rera.number}</p>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default LeadFormModal;
